import React from 'react';
import styled from 'styled-components';

interface EnterpriseCardProps {
  enterprise: {
    id: string;
    name: string;
    category: string;
    logo?: string;
    creators: number;
    active: number;
    topEarnings: number;
  };
  isFollowed?: boolean;
  onFollow: (id: string) => void;
  onDetails: (id: string) => void;
  onSubmitVideo: (enterpriseName: string) => void;
}

const Card = styled.div`
  background-color: #1C1628;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: 0;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const CardContent = styled.div`
  padding: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px;
    position: relative;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 16px;
  }
`;

const EnterpriseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Logo = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  background-color: #FF6B00;
  color: white;
  font-size: 28px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  &.travel {
    background-color: #FF6B00;
  }
  
  &.saas {
    background-color: #3B82F6;
  }
  
  &.luxury {
    background-color: #DC2626;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Category = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  text-transform: capitalize;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 12px;
  }
`;

const Name = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 18px;
  }
`;

const DetailsButton = styled.button`
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const FollowButton = styled.button<{ $isFollowed: boolean }>`
  background-color: ${({ $isFollowed }) => $isFollowed ? 'transparent' : '#9333EA'};
  border: 1px solid ${({ $isFollowed }) => $isFollowed ? 'rgba(255, 255, 255, 0.1)' : '#9333EA'};
  color: white;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background-color: ${({ $isFollowed }) => $isFollowed ? 'transparent' : '#7C3AED'};
    border-color: ${({ $isFollowed }) => $isFollowed ? 'rgba(255, 255, 255, 0.2)' : '#7C3AED'};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: absolute;
    top: 16px;
    right: 16px;
    width: auto;
    
    button {
      padding: 6px 10px;
      font-size: 12px;
    }
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    padding: 12px 16px;
    gap: 8px;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 0;
  }
`;

const StatsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 1px;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: 16px;
    flex: 1;
    width: 50%;
    
    &::after {
      display: block;
      height: 100%;
    }
  }
`;

const TopEarnersSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
`;

const TopEarners = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 8px;
`;

const Amount = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: baseline;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 20px;
  }
`;

const Period = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 4px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  order: 1;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #fff;
  order: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 20px;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 40px;
`;

const VideoSubmitSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-left: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-left: 16px;
    padding-top: 0;
    border-top: none;
    flex: 1;
    width: 50%;
  }
`;

const VideoIcon = styled.div`
  margin-bottom: 12px;
  color: #9333EA;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-bottom: 8px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const VideoTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #fff;
  margin-bottom: 4px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 14px;
  }
`;

const VideoSubtitle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 24px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 12px;
    margin-bottom: 12px;
  }
`;

const SubmitButton = styled.button`
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #9333EA;
  border-radius: 8px;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 500;
  width: 100%;
  cursor: pointer;
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 8px 0;
    font-size: 12px;
  }
`;

const EnterpriseCard: React.FC<EnterpriseCardProps> = ({
  enterprise,
  isFollowed,
  onFollow,
  onDetails,
  onSubmitVideo
}) => {
  const { id, name, category, logo, topEarnings, creators, active } = enterprise;
  
  const formatCurrency = (amount: number): string => {
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}k`;
    }
    return `$${amount}`;
  };
  
  // Get first letter of enterprise name for logo fallback
  const logoLetter = name.charAt(0).toUpperCase();
  
  // Determine logo class based on category
  const logoClass = category.toLowerCase();
  
  return (
    <Card>
      <CardContent>
        <Header>
          <EnterpriseInfo>
            <Logo className={logoClass}>
              {logo ? <img src={logo} alt={`${name} logo`} /> : logoLetter}
            </Logo>
            <Details>
              <Category>{category}</Category>
              <Name>{name}</Name>
            </Details>
          </EnterpriseInfo>
          <ButtonGroup>
            <DetailsButton onClick={() => onDetails(id)}>Details</DetailsButton>
            <FollowButton 
              $isFollowed={!!isFollowed}
              onClick={() => onFollow(id)}
            >
              {isFollowed ? 'Unfollow' : 'Follow'}
            </FollowButton>
          </ButtonGroup>
        </Header>
        
        <StatsSection>
          <StatsGroup>
            <TopEarnersSection>
              <TopEarners>Top Earners</TopEarners>
              <Amount>
                {formatCurrency(topEarnings)}
                <Period>/month</Period>
              </Amount>
            </TopEarnersSection>
            
            <StatsRow>
              <Stat>
                <StatLabel>Creators</StatLabel>
                <StatValue>{creators}</StatValue>
              </Stat>
              
              <Stat>
                <StatLabel>Active+</StatLabel>
                <StatValue>{active}</StatValue>
              </Stat>
            </StatsRow>
          </StatsGroup>
          
          <VideoSubmitSection>
            <VideoIcon>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#9333EA" strokeWidth="2"/>
                <path d="M15 12L10 15V9L15 12Z" fill="#9333EA"/>
              </svg>
            </VideoIcon>
            <VideoTitle>Submit a video</VideoTitle>
            <VideoSubtitle>Avg approval time: 1 hour</VideoSubtitle>
            <SubmitButton onClick={() => onSubmitVideo(name)}>
              Submit
            </SubmitButton>
          </VideoSubmitSection>
        </StatsSection>
      </CardContent>
    </Card>
  );
};

export default EnterpriseCard;
